import React, { useEffect } from "react"
import "./Layout.css"
import AldooBoot from "aldoo-ra/Boot"
import Site from "../components/Site"
import CheckoutHost from "../components/Storefront/checkout/CheckoutHost"
import PlayerHost from "../components/Storefront/player"
import SupportChat from "../components/Storefront/SupportChat"
import AgentPresenceBeat from "aldoo-ra/Admin/CustomerSupport/AgentPresenceBeat"
import SiteLinks from "../components/Storefront/SiteLinks"
import CartRail from "../components/Storefront/ui/cart/CartRail"
import AldooAnalytics from "../components/Storefront/AldooAnalytics"
import ConsentBanner from "../components/Storefront/ConsentBanner"
import { VariantProvider } from "aldoo-ra/VariantReader/VariantContext.jsx"
import { usePageContext } from "vike-react/usePageContext"
import { captureUTM } from "../components/Storefront/checkout/utm"

// Storefront variants (components/Variants/*.jsx) are picked up by
// VariantReader's glob; this map is only for CMS-fallback overrides.
const localVariants = {}

// Merge SSR/CMS variants with local file variants.
// Local variants fill in any empty or missing values from the server,
// so bad/incomplete CMS data never overrides correct local definitions.
function mergeWithLocalFallback(ssrVariants) {
  const result = {}
  const names = new Set([
    ...Object.keys(ssrVariants || {}),
    ...Object.keys(localVariants),
  ])
  for (const name of names) {
    const server = ssrVariants?.[name] || {}
    const local = localVariants[name] || {}
    const merged = { ...server }
    for (const [key, value] of Object.entries(local)) {
      if (!merged[key]) merged[key] = value
    }
    result[name] = merged
  }
  return result
}

const SCROLL_KEY = "aldoo_scroll_y"

export function Layout({ children }) {
  const pageContext = usePageContext()
  const variants = mergeWithLocalFallback(pageContext.ssrVariants)
  // a page of its own on a phone (signing from a QR code): no cart, chat or consent over it
  const bare = /^\/([a-z]{2}\/)?sign-on-phone(\/|$)/.test(pageContext.urlPathname || "")

  // UTM params are captured on every navigation, not only on the first load
  useEffect(() => {
    captureUTM()
  }, [pageContext.urlPathname, pageContext.urlParsed?.searchOriginal])

  useEffect(() => {
    if ("scrollRestoration" in history) {
      history.scrollRestoration = "manual"
    }

    const saved = localStorage.getItem(SCROLL_KEY)
    if (saved !== null) {
      const target = parseInt(saved, 10)
      const deadline = Date.now() + 3000
      const tryScroll = () => {
        if (document.body.scrollHeight >= target + window.innerHeight) {
          window.scrollTo(0, target)
        } else if (Date.now() < deadline) {
          requestAnimationFrame(tryScroll)
        }
      }
      if (document.readyState === "complete") {
        tryScroll()
      } else {
        window.addEventListener("load", tryScroll, { once: true })
      }
    }

    let ticking = false
    const onScroll = () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          localStorage.setItem(SCROLL_KEY, String(window.scrollY))
          ticking = false
        })
        ticking = true
      }
    }

    window.addEventListener("scroll", onScroll, { passive: true })
    return () => window.removeEventListener("scroll", onScroll)
  }, [])

  return (
    <React.StrictMode>
      <VariantProvider ssrVariants={variants}>
        <AldooBoot />
        {/* Google Analytics 4 / Ads go through aldoo-ra Tracking/google (in AldooBoot), the tag rendered with the page */}
        <AldooAnalytics />
        {/* Tag Manager for a visitor without JavaScript: in the body, as Google asks */}
        {pageContext.tracking?.gtmId && /^GTM-[A-Z0-9]{4,12}$/.test(pageContext.tracking.gtmId) && (
          <noscript>
            <iframe src={`https://www.googletagmanager.com/ns.html?id=${pageContext.tracking.gtmId}`} height="0" width="0" style={{ display: "none", visibility: "hidden" }} title="gtm" />
          </noscript>
        )}
        <SiteLinks>
          <Site>{children}</Site>
          {/* the cart at the edge of a wide screen (not in /admin, not in the merchant portal) */}
          {!bare && !/^\/([a-z]{2}\/)?(admin|vendor)(\/|$)/.test(pageContext.urlPathname || "") && <CartRail />}
        </SiteLinks>
        {/* aldoo-ra Store (cart drawer, checkout flow "aldoo", success/error) + storefront plugins */}
        <CheckoutHost />
        <PlayerHost />
        {/* a signed-in support agent is "Online" to the chat from any page, /admin included */}
        <AgentPresenceBeat />
        {/* the customer widget belongs to the storefront, not to /admin */}
        {!bare && !/^\/([a-z]{2}\/)?admin(\/|$)/.test(pageContext.urlPathname || "") && <SupportChat />}
        {/* cookie consent: where it is asked (the visitor's country, TRACKING_CONSENT), and only on the store front */}
        {!bare && !/^\/([a-z]{2}\/)?(admin|vendor)(\/|$)/.test(pageContext.urlPathname || "") && <ConsentBanner />}
      </VariantProvider>
    </React.StrictMode>
  )
}
